import DashboardService from "../../services/dashboard-service";
import md5 from "md5";
import { TERMINAL_MODE } from "@/helpers/terminal/mode";
import { TAX_OPTION } from "@/helpers/store/tax_options";
import indexDbService from "@/helpers/dbHelper";
import {
  updateWhitelabelTheme,
  resetWhitelabelTheme,
} from "@/helpers/whitelabelHelper";
import liveObjectsService from "@/plugins/liveObjects";

const clonePayload = (payload) => {
  if (!payload) return payload;
  try {
    return JSON.parse(JSON.stringify(payload));
  } catch (error) {
    console.warn("[Config] Failed to clone payload for broadcast", error);
    if (Array.isArray(payload)) {
      return [...payload];
    }
    return { ...payload };
  }
};

const extractRealtimeMeta = (payload) => {
  if (!payload) {
    return { cleanPayload: payload, fromRealtime: false };
  }
  // Arrays are always treated as pure data; they cannot carry meta flags
  if (Array.isArray(payload)) {
    return { cleanPayload: payload, fromRealtime: false };
  }
  if (typeof payload !== "object") {
    return { cleanPayload: payload, fromRealtime: false };
  }
  const { __fromRealtime, ...cleanPayload } = payload;
  return { cleanPayload, fromRealtime: !!__fromRealtime };
};

const getArrayFromPayload = (payload, key) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  const value = payload[key];
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const broadcastTablesReplace = (tables) => {
  if (!liveObjectsService.isReady()) return;
  const payload = {
    source: liveObjectsService.getClientId(),
    tables: clonePayload(tables),
  };
  liveObjectsService.publishTablesReplace(payload);
};

const broadcastTableUpdate = (table) => {
  if (!liveObjectsService.isReady()) return;
  const payload = {
    source: liveObjectsService.getClientId(),
    table: clonePayload(table),
  };
  liveObjectsService.publishTableUpdate(payload);
};

const broadcastSectionsUpdate = (sections) => {
  if (!liveObjectsService.isReady()) return;
  const payload = {
    source: liveObjectsService.getClientId(),
    sections: clonePayload(sections),
  };
  liveObjectsService.publishSectionsUpdate(payload);
};

export const config = {
  namespaced: true,
  state: {
    baseUrl: null,
    token: null,
    terminal: null,
    store: null,
    tax: null,
    tableUpdatedLocally: false,
    store_terminals: [],
    sections: [],
    tables: [],
    products: [],
    categories: [],
    employees: [],
    discounts: [],
    void_reasons: [],
    currency: {},
    kitchenhub: null,
  },
  getters: {
    baseUrl: (state) => {
      return state.baseUrl;
    },
    isKioskMode: (state) => {
      return state.terminal && state.terminal.mode
        ? TERMINAL_MODE[state.terminal.mode] === "KIOSK_MODE"
        : false;
    },
    token: (state) => {
      return state.token;
    },
    terminal: (state) => {
      return state.terminal;
    },
    store: (state) => {
      return state.store;
    },
    isTaxInclusive: (state) => {
      return Number(state.store.tax_option) === TAX_OPTION.TAX_INCLUSIVE;
    },
    tax: (state) => {
      // Return null if tax is not configured (expected for stores without tax)
      return state.tax || null;
    },
    sections: (state) => {
      return state.sections;
    },
    discounts: (state) => {
      return state.discounts;
    },
    voidReasons: (state) => {
      return state.void_reasons;
    },
    kitchenhub: (state) => {
      return state.kitchenhub;
    },
    products: (state) => {
      return state.products.sort(function (a, b) {
        return a.sorting - b.sorting;
      });
    },
    product: (state) => (id) => {
      return state.products.find((p) => p.id === id);
    },
    categories: (state) => {
      return state.categories
        .sort(function (a, b) {
          return a.sorting - b.sorting;
        })
        .filter((category) => category.id !== -2); // do not show gift card category
    },
    paymentTypes: (state) => {
      let payment_types = state.terminal.payment_types;
      const is_kiosk = TERMINAL_MODE[state.terminal.mode] === "KIOSK_MODE";
      payment_types = payment_types.filter((pt) =>
        is_kiosk ? pt.is_at_kiosk : pt.is_at_pos,
      );
      return payment_types;
    },
    employeeById: (state) => (id) => {
      return state.employees.find((item) => item.id === id);
    },
    employees: (state) => state.employees,
    getTableById: (state) => (id) => {
      const parsedId = parseInt(id);
      // First try to find in state.tables
      let table = state.tables.find((t) => t.id === parsedId);
      // If not found, search in sections (tables are nested inside sections)
      if (!table) {
        for (const section of state.sections) {
          if (section.tables) {
            table = section.tables.find((t) => t.id === parsedId);
            if (table) break;
          }
        }
      }
      return table;
    },
    currency: (state) => {
      return state.currency.symbol || "$";
    },
    storeTerminals: (state) => {
      return state.store_terminals;
    },
    getAllTables: (state) => state.tables,
    enableOnlineOrders: (state) => {
      let setting = undefined;
      setting = state.terminal.db_settings.find(
        (setting) => setting.name === "online_order",
      )?.value; // the value of 'value' is string and not boolean i.e. it is "true" and not true
      // checks for false or undefined will hide online orders while true or null will show online orders
      if (setting === "true" || setting === null) return "true";
      else return "false";
    },
    kioskAskName: (state) => {
      if (state.terminal && state.terminal.db_settings) {
        const setting = state.terminal.db_settings.find(
          (s) => s.name === "kiosk_ask_name",
        );

        return setting ? setting.value === "true" : true;
      }
      return true;
    },
    kioskDisableLogoText: (state) => {
      if (!state.terminal || !state.terminal.db_settings) return false;
      const setting = state.terminal.db_settings.find(
        (s) => s.name === "kiosk_disable_logo_text",
      );
      return setting && setting.value === "true";
    },
    isLocalization: (state) => {
      return state.store && state.store.country === "US";
    },
    isWhitelabelEnabled: (state) => {
      if (!state.terminal || !state.terminal.db_settings) return false;
      const whitelabelSetting = state.terminal.db_settings.find(
        (setting) => setting.name === "whitelabel",
      );
      return whitelabelSetting && whitelabelSetting.value === "true";
    },
    autoLogout: (state) => {
      if (!state.terminal || !state.terminal.db_settings) return false;
      const setting = state.terminal.db_settings.find(
        (setting) => setting.name === "auto_logout",
      );
      return setting && setting.value === "true";
    },
    whitelabelSettings: (state) => {
      if (!state.terminal || !state.terminal.db_settings) return null;
      const whitelabelSetting = state.terminal.db_settings.find(
        (setting) => setting.name === "whitelabel",
      );
      if (!whitelabelSetting || whitelabelSetting.value !== "true") return null;

      // Check if description is a property of the whitelabel setting
      const descriptionValue =
        whitelabelSetting.description || whitelabelSetting.value;

      // Also check for separate description setting
      const descriptionSetting = state.terminal.db_settings.find(
        (setting) => setting.name === "description",
      );
      const finalDescription =
        descriptionValue || (descriptionSetting && descriptionSetting.value);

      if (!finalDescription) return null;

      try {
        const parsed = JSON.parse(finalDescription);
        return parsed;
      } catch (e) {
        console.error("Failed to parse whitelabel description:", e);
        return null;
      }
    },
    primaryColor: (state, getters) => {
      const whitelabelSettings = getters.whitelabelSettings;
      return whitelabelSettings ? whitelabelSettings.primary_color : "#007bff";
    },
    whitelabelLogo: (state, getters) => {
      const whitelabelSettings = getters.whitelabelSettings;
      return whitelabelSettings ? whitelabelSettings.logo : null;
    },
    printers: (state) => {
      // Return printers array from terminal object, or empty array if not available
      return state.terminal && state.terminal.printers
        ? state.terminal.printers
        : [];
    },
    kioskVerticalModifiers: (state) => {
      if (!state.terminal || !state.terminal.db_settings) return false;
      const setting = state.terminal.db_settings.find(
        (s) => s.name === "kiosk_vertical_modifiers",
      );
      if (!setting) return false;
      const v = setting.value;
      if (v === true) return true;
      if (v === false || v == null) return false;
      if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        if (s === "true") return true;
        if (s === "false") return false;
      }
      return false;
    },
    modifierGroupVertical: (state) => {
      if (!state.terminal || !state.terminal.db_settings) return false;
      // Support legacy typo key (`modifier_group_veritcal`) as well as the correct one.
      const setting =
        state.terminal.db_settings.find(
          (s) => s.name === "modifier_group_vertical",
        ) ||
        state.terminal.db_settings.find(
          (s) => s.name === "modifier_group_veritcal",
        );

      if (!setting) return false;

      const v = setting.value;
      if (v === true) return true;
      if (v === false || v == null) return false;

      // Strict behavior: only accept boolean true/false or string "true"/"false"
      if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        if (s === "true") return true;
        if (s === "false") return false;
      }

      return false;
    },
  },
  mutations: {
    setBaseUrl(state, baseUrl) {
      state.baseUrl = baseUrl;
      localStorage.setItem("baseUrl", baseUrl);
    },
    setToken(state, token) {
      state.token = token;
      localStorage.setItem("token", state.token);
    },
    setTerminal(state, terminal) {
      state.terminal = terminal;
      localStorage.setItem("terminal", JSON.stringify(state.terminal));

      // Apply whitelabel theming if enabled
      if (terminal && terminal.db_settings) {
        const whitelabelSetting = terminal.db_settings.find(
          (setting) => setting.name === "whitelabel",
        );
        if (whitelabelSetting && whitelabelSetting.value === "true") {
          // Check if description is a property of the whitelabel setting
          const descriptionValue =
            whitelabelSetting.description || whitelabelSetting.value;

          // Also check for separate description setting
          const descriptionSetting = terminal.db_settings.find(
            (setting) => setting.name === "description",
          );
          const finalDescription =
            descriptionValue ||
            (descriptionSetting && descriptionSetting.value);

          if (finalDescription) {
            try {
              const whitelabelSettings = JSON.parse(finalDescription);
              if (whitelabelSettings && whitelabelSettings.primary_color) {
                updateWhitelabelTheme(whitelabelSettings.primary_color);
              }
            } catch (e) {
              console.error("Failed to parse whitelabel description:", e);
              resetWhitelabelTheme();
            }
          }
        } else {
          resetWhitelabelTheme();
        }
      } else {
        resetWhitelabelTheme();
      }
    },
    setStore(state, payload) {
      state.store = payload;
      if (payload && payload.subdomain) {
        localStorage.setItem("store_subdomain", payload.subdomain);
      }
    },
    setTax(state, payload) {
      state.tax = payload;
    },
    setCurrency(state, payload) {
      state.currency = payload;
      indexDbService.saveToDb("currency", state.currency).then();
    },
    setSections(state, payload) {
      const { cleanPayload, fromRealtime } = extractRealtimeMeta(payload);
      const sections = getArrayFromPayload(cleanPayload, "sections");
      state.sections = sections;
      state.sections.forEach((section) =>
        indexDbService.saveToDb("sections", section).then(),
      );
      if (!fromRealtime) {
        broadcastSectionsUpdate(sections);
      }
    },
    setDiscounts(state, payload) {
      state.discounts = payload;
      state.discounts.forEach((discounts) =>
        indexDbService.saveToDb("discounts", discounts).then(),
      );
    },
    setVoidReasons(state, payload) {
      state.void_reasons = Array.isArray(payload) ? payload : [];
      state.void_reasons.forEach((voidReason) =>
        indexDbService.saveToDb("void_reasons", voidReason).then(),
      );
    },
    setKitchenHub(state, payload) {
      state.kitchenhub = payload;
      if (payload) {
        indexDbService.saveToDb("kitchenhub", payload).then();
      }
    },
    async setTables(state, payload) {
      const { cleanPayload, fromRealtime } = extractRealtimeMeta(payload);
      const tables = getArrayFromPayload(cleanPayload, "tables");
      state.tables = tables;
      if (!fromRealtime) {
        broadcastTablesReplace(tables);
      }
    },
    async setTable(state, payload) {
      if (!payload) return;
      const { cleanPayload, fromRealtime } = extractRealtimeMeta(payload);
      const index = state.tables.findIndex((t) => t.id == cleanPayload.id);
      if (index === -1) {
        return;
      }
      state.tables[index] = { ...cleanPayload, ...{ updated: true } };
      state.tableUpdatedLocally = true;
      await indexDbService.saveToDb("tables", state.tables[index]);
      // update at section
      const sIndex = state.sections.findIndex(
        (s) => s.id === cleanPayload.section,
      );
      if (sIndex > -1) {
        const tIndex = state.sections[sIndex].tables.findIndex(
          (t) => t.id === cleanPayload.id,
        );
        if (tIndex > -1) {
          state.sections[sIndex].tables[tIndex] = cleanPayload;
          await indexDbService.saveToDb("sections", state.sections[sIndex]);
        }
      }
      if (!fromRealtime) {
        broadcastTableUpdate(state.tables[index]);
      }
    },
    setTableUpdatedLocally(state, payload) {
      state.tableUpdatedLocally = payload;
    },
    setProducts(state, payload) {
      state.products = payload;
    },
    async setCategories(state, payload) {
      state.categories = payload;
    },
    setEmployees(state, payload) {
      state.employees = payload;
    },
    setStoreTerminals(state, payload) {
      state.store_terminals = payload;
    },
  },
  actions: {
    getProductsByCategoryId: ({ state }, payload) => {
      return state.products.filter((item) => item.id == payload);
    },
    getEmployeeByHashedPin: ({ state }, pin) => {
      const hashedPin = md5(pin);
      return state.employees.find((item) => item.uid === hashedPin);
    },
    setAuthConfig: async ({ commit }, authSettings) => {
      commit("setBaseUrl", authSettings.url);
      commit("setToken", authSettings.token);
      commit("setTax", authSettings.taxn);
      await indexDbService.saveToDb("tax", authSettings.taxn);
    },
    resetAuthConfig: async ({ commit }) => {
      commit("setBaseUrl", "");
      commit("setToken", "");
      commit("setTax", "");
      await indexDbService.saveToDb("tax", "");
    },
    fetchTerminal: async ({ commit, dispatch, getters }) => {
      if (window.navigator.onLine) {
          try {
              const terminalConfig = await DashboardService.getTerminalConfig();
              commit("setTerminal", terminalConfig["terminal"]);
              dispatch(
                "customerDisplay/sendMessage",
                { whitelabel: getters.whitelabelSettings },
                { root: true },
              );
              commit("setCurrency", terminalConfig["currency"]);
              commit("setStore", terminalConfig["store"]);
              commit("setTax", terminalConfig["tax"]);
              commit("setStoreTerminals", terminalConfig["store_terminals"]);
              // Replace the cached void reasons wholesale so archived/removed
              // reasons don't linger in IndexedDB for offline use.
              await indexDbService.clearAllData("void_reasons");
              commit("setVoidReasons", terminalConfig["void_reasons"] || []);
              await indexDbService.saveToDb("store", terminalConfig["store"]);

              if (terminalConfig["tax"] && terminalConfig["tax"].id) {
                  await indexDbService.saveToDb("tax", terminalConfig["tax"]);
              } else {
                  console.warn("Skipping tax save to DB: Tax object is missing or has no ID", terminalConfig["tax"]);
                  // Optional: If you need to clear old tax from DB when none exists
                  // await indexDbService.clearStore("tax");
              }
          } catch (e) {
              // Don't throw - allow app to continue with cached terminal config from IndexedDB
          }

      }
    },
    fetchKitchenHubConfig: async ({ commit }) => {
      if (window.navigator.onLine) {
        try {
          const kitchenHubConfig = await DashboardService.getKitchenHubConfig();
          commit("setKitchenHub", {
            id: 1,
            client_id: kitchenHubConfig.client_id,
            client_secret: kitchenHubConfig.client_secret,
          });
        } catch (e) {
          // Not configured for this store, or request failed - leave any cached value as-is
        }
      }
    },
    fetchSections: async ({ commit }) => {
      if (window.navigator.onLine) {
        try {
          const retrieved = (await DashboardService.getSections()).results;
          commit("setSections", retrieved);
        } catch (error) {
          console.error("Failed to fetch sections:", error);
          // Don't throw - allow app to continue with cached sections from IndexedDB
        }
      }
    },
    fetchTables: async () => {
      if (window.navigator.onLine) {
        try {
          // const retrieved = (await DashboardService.getTables()).results;
          // await retrieved.forEach(async table => await indexDbService.saveToDb('tables', table))
          // commit("setTables", retrieved);
        } catch (error) {
          console.error("Failed to fetch tables:", error);
          // Don't throw - allow app to continue with cached tables from IndexedDB
        }
      }
    },
    fetchCategories: async ({ commit }) => {
      if (window.navigator.onLine) {
        const retrieved = (await DashboardService.getCategories()).results;
        await retrieved.forEach(
          async (category) =>
            await indexDbService.saveToDb("categories", category),
        );
        const categories = await indexDbService.getDbData("categories");
        await commit("setCategories", categories);
      }
    },
    fetchDiscounts: async ({ commit }) => {
      if (window.navigator.onLine) {
        const retrieved = (await DashboardService.getDiscounts()).results;
        await retrieved.forEach(
          async (discounts) =>
            await indexDbService.saveToDb("discounts", discounts),
        );
        commit("setDiscounts", retrieved);
      }
    },
    fetchProducts: async ({ commit, dispatch }) => {
      if (window.navigator.onLine) {
        let response;
        let retrieved;
        let page = 1;
        commit("setSyncDetail", { item: "products", page }, { root: true });
        response = await DashboardService.getProducts();
        retrieved = response.results;
        while (response.next) {
          page += 1;
          commit("setSyncDetail", { item: "products", page }, { root: true });
          response = await DashboardService.getNextProducts(
            response.next.split("/api")[1],
          );
          retrieved = [...retrieved, ...response.results];
        }
        await retrieved.forEach(async (product) =>
          indexDbService.saveToDb("products", product),
        );
        commit("setProducts", retrieved);
        dispatch("products86d/hydrateFromProducts", retrieved, { root: true });
      }
    },
    fetchEmployees: async ({ commit }) => {
      if (window.navigator.onLine) {
        const retrieved = (await DashboardService.getEmployees()).results;
        await retrieved.forEach(
          async (employee) =>
            await indexDbService.saveToDb("employee", employee),
        );
        commit("setEmployees", retrieved);
      }
    },
    uploadUpdatedTables: async ({ state, commit }) => {
      commit("setTableUpdatedLocally", false);
      // filter updated
      const updatedTables = state.tables.filter((t) => t.updated);

      state.tables.map(function (x) {
        x.updated = false;
        return x;
      });
      if (updatedTables.length) {
        const response = await DashboardService.postTablePositions(
          updatedTables,
        );
        if (response && !state.tableUpdatedLocally) {
          commit("setTableUpdatedLocally", false);
        }
      }
    },
    async fetchProductsFromDb({ commit, dispatch }) {
      const products = await indexDbService.getDbData("products");
      commit("setProducts", products);
      dispatch("products86d/hydrateFromProducts", products, { root: true });
    },
    async fetchCategoriesFromDb({ commit }) {
      const categories = await indexDbService.getDbData("categories");
      commit("setCategories", categories);
    },
    async fetchEmployeesFromDb({ commit }) {
      const employees = await indexDbService.getDbData("employee");
      commit("setEmployees", employees);
    },
    async fetchDiscountsFromDb({ commit }) {
      const discounts = await indexDbService.getDbData("discounts");
      commit("setDiscounts", discounts);
    },
    async fetchVoidReasonsFromDb({ commit }) {
      const voidReasons = await indexDbService.getDbData("void_reasons");
      commit("setVoidReasons", voidReasons);
    },
    async fetchTablesFromDb({ commit }) {
      const tables = await indexDbService.getDbData("tables");
      commit("setTables", tables);
    },
    async fetchSectionsFromDb({ commit }) {
      const sections = await indexDbService.getDbData("sections");
      commit("setSections", sections);
    },
    async fetchTaxFromDb({ commit }) {
      const tax = await indexDbService.getDbData("tax");
      // Handle case where tax might be empty array or null (store has no tax configured)
      const taxValue = tax && tax.length > 0 ? tax[0] : null;
      commit("setTax", taxValue);
    },
    applyRealtimeTable({ state, commit }, payload) {
      if (!payload || !payload.table) return;
      if (state.tableUpdatedLocally) return;
      commit("setTable", { ...payload.table, __fromRealtime: true });
    },
    applyRealtimeTablesReplace({ state, commit }, payload) {
      if (!payload || !payload.tables) return;
      if (state.tableUpdatedLocally) return;
      commit("setTables", { tables: payload.tables, __fromRealtime: true });
    },
    applyRealtimeSectionsUpdate({ commit }, payload) {
      if (!payload || !payload.sections) return;
      commit("setSections", {
        sections: payload.sections,
        __fromRealtime: true,
      });
    },
  },
};
